import React from 'react';

import AddIcon from '@atlaskit/icon/core/add';
import AgentIcon from '@atlaskit/icon/core/ai-agent';
import HeartIcon from '@atlaskit/icon/core/heart';
import PersonIcon from '@atlaskit/icon/core/person';
import TeamsIcon from '@atlaskit/icon/core/teams';
import { useIntl } from 'react-intl';

import { AnalyticsSideNavItem } from '@townsquare/analytics-nav4';
import { useRovoWorkspace } from '@townsquare/collab-context';
import { useCreateTeamModal } from '@townsquare/create-team-modal';
import { filterUndefined } from '@townsquare/filter-type';
import { SideNavItem } from '@townsquare/griffin-navigation/types';
import { useInviteUserToSiteModal } from '@townsquare/invite-user-modal/site';
import { useKudosLauncher } from '@townsquare/kudos-on-feed/useKudosLauncher';
import { SideNavProductItem } from '@townsquare/navigation-config/nav4';
import { TEAM_SEARCH_SCREEN_TYPES } from '@townsquare/ptc-directory-view/screen-config';
import { useRouterActions } from '@townsquare/router/primitives/actions';
import { useRouteDependencies } from '@townsquare/router/primitives/dependencies';
import { generatePath } from '@townsquare/router/primitives/path';
import { agentProfileRoute } from '@townsquare/routes/agent-profile';
import { kudosDirectoryRoute } from '@townsquare/routes/kudos-directory-view';
import { kudosProfileRoute } from '@townsquare/routes/kudos-profile';
import { peopleDirectoryRoute } from '@townsquare/routes/people-directory-view';
import { profileRouteTeamsMatch } from '@townsquare/routes/profile';
import { teamProfileRoute } from '@townsquare/routes/team-profile';
import { teamsAppAgentsDirectoryRoute } from '@townsquare/routes/teams-app-agents-directory-view';
import { teamsDirectoryRoute } from '@townsquare/routes/teams-directory-view';
import { useRovoEntitlement } from '@townsquare/rovo/entitlement';
import { useNoWorkspaceExperience } from '@townsquare/workspace-store';

import { MenuItem, MenuItemProps } from './MenuItem';

export const useSideNavItems = (): SideNavItem[] => {
	const intl = useIntl();
	const hasNoWorkspace = useNoWorkspaceExperience();
	const routeDependencies = useRouteDependencies();
	const { pushTo } = useRouterActions();
	const cloudId = useRovoWorkspace();
	const rovoEntitlement = useRovoEntitlement();

	const { openCreateTeamModal } = useCreateTeamModal({
		analyticsCtx: { source: 'teamsAppSideNav' },
		onCreate: (team) => pushTo(teamProfileRoute, { params: { id: team.id } }),
	});

	const { canInviteUserToSite, openInviteUserToSiteModal } = useInviteUserToSiteModal({
		cloudId,
		analyticsSource: 'peopleDirectoryHome',
		enabled: true,
	});

	const { openKudosLauncher, kudosLauncher } = useKudosLauncher({
		analyticsSource: 'TeamsAppSideNav',
	});

	return [
		{
			id: SideNavProductItem.TEAMS_BROWSE_TEAMS,
			analyticsSubject: AnalyticsSideNavItem.TEAMS_BROWSE_TEAMS,
			path: `${generatePath(teamsDirectoryRoute, routeDependencies)}/?&screen=SEARCH_TEAMS`,
			matches: [teamProfileRoute],
			Component: (props: MenuItemProps) => (
				<MenuItem
					{...props}
					matchingDirectoryScreens={TEAM_SEARCH_SCREEN_TYPES}
					item={{
						...props.item,
						hoverAction: { Icon: AddIcon, label: '', onClick: openCreateTeamModal },
					}}
				/>
			),
			isDraggable: true,
			name: intl.formatMessage({
				id: 'townsquare.teams.use-side-nav-items.teams',
				description: 'Side nav button to open teams search page',
				defaultMessage: 'Teams',
			}),
			Icon: TeamsIcon,
			testId: 'side-nav-teams',
		},
		{
			id: SideNavProductItem.TEAMS_BROWSE_PEOPLE,
			analyticsSubject: AnalyticsSideNavItem.TEAMS_BROWSE_PEOPLE,
			path: `${generatePath(peopleDirectoryRoute, routeDependencies)}/?&screen=SEARCH_PEOPLE`,
			matches: [profileRouteTeamsMatch],
			Component: (props: MenuItemProps) => (
				<MenuItem
					{...props}
					// Added new screen types to highlight the side nav item when redirecting from other screens
					matchingDirectoryScreens={[
						'SEARCH_PEOPLE',
						'BROWSE_DEPARTMENTS',
						'BROWSE_LOCATIONS',
						'BROWSE_JOB_TITLES',
					]}
					item={{
						...props.item,
						hoverAction: canInviteUserToSite
							? { Icon: AddIcon, label: '', onClick: openInviteUserToSiteModal }
							: undefined,
					}}
				/>
			),
			isDraggable: true,
			name: intl.formatMessage({
				id: 'townsquare.atlassian-home.side-nav.people',
				description: 'Side nav button to open people search page',
				defaultMessage: 'People',
			}),
			Icon: PersonIcon,
			testId: 'side-nav-people',
		},
		rovoEntitlement?.data?.isRovoLLMEnabled
			? {
					id: SideNavProductItem.TEAMS_BROWSE_AGENTS,
					analyticsSubject: AnalyticsSideNavItem.TEAMS_BROWSE_PEOPLE,
					path: `${generatePath(teamsAppAgentsDirectoryRoute, routeDependencies)}/?&screen=SEARCH_AGENTS`,
					matches: [teamsAppAgentsDirectoryRoute, agentProfileRoute],
					Component: (props: MenuItemProps) => (
						<MenuItem
							{...props}
							// Added new screen types to highlight the side nav item when redirecting from other screens
							matchingDirectoryScreens={['SEARCH_AGENTS']}
						/>
					),
					isDraggable: true,
					name: intl.formatMessage({
						id: 'townsquare.teams.use-side-nav-items.agents',
						description: 'Side nav button to open agents search page',
						defaultMessage: 'Agents',
					}),
					Icon: AgentIcon,
					testId: 'side-nav-agents',
				}
			: undefined,
		hasNoWorkspace
			? undefined
			: {
					id: SideNavProductItem.TEAMS_BROWSE_KUDOS,
					analyticsSubject: AnalyticsSideNavItem.TEAMS_BROWSE_KUDOS,
					path: `${generatePath(kudosDirectoryRoute, routeDependencies)}/?&screen=BROWSE_KUDOS`,
					matches: [kudosProfileRoute],
					Component: (props: MenuItemProps) => (
						<>
							<MenuItem
								{...props}
								matchingDirectoryScreens={['BROWSE_KUDOS']}
								item={{
									...props.item,
									hoverAction: { Icon: AddIcon, label: '', onClick: openKudosLauncher },
								}}
							/>
							{kudosLauncher}
						</>
					),
					isDraggable: true,
					name: intl.formatMessage({
						id: 'townsquare.atlassian-home.side-nav.kudos',
						description: 'Side nav button to open kudos search page',
						defaultMessage: 'Kudos',
					}),
					Icon: HeartIcon,
					testId: 'side-nav-kudos',
				},
	].filter(filterUndefined);
};
